import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2.5 whitespace-nowrap text-sm font-semibold ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground rounded-xl shadow-sm hover:shadow-md hover:bg-navy-light active:scale-[0.98]",
        destructive: "bg-destructive text-destructive-foreground rounded-xl hover:bg-destructive/90",
        outline: "border-2 border-border bg-transparent rounded-xl hover:bg-muted hover:border-muted-foreground/20",
        secondary: "bg-secondary text-secondary-foreground rounded-xl hover:bg-secondary/80",
        ghost: "rounded-xl hover:bg-muted hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        // Premium Tiego variants
        hero: "bg-gradient-to-r from-gold to-gold-dark text-white font-bold rounded-xl shadow-gold hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] border border-gold-light/20",
        heroOutline: "border-2 border-white/20 text-white bg-white/5 backdrop-blur-sm rounded-xl hover:bg-white/10 hover:border-white/30 active:scale-[0.98]",
        gold: "bg-gradient-to-r from-gold to-gold-dark text-white font-semibold rounded-xl shadow-sm hover:shadow-gold hover:scale-[1.01] active:scale-[0.99]",
        subtle: "bg-muted/80 text-muted-foreground rounded-xl hover:bg-muted hover:text-foreground",
        premium: "bg-navy-deep text-white font-semibold rounded-xl shadow-md hover:shadow-lg hover:bg-navy active:scale-[0.98]",
      },
      size: {
        default: "h-11 px-5 py-2.5",
        sm: "h-9 rounded-lg px-4 text-xs",
        lg: "h-13 px-8 text-base rounded-xl",
        xl: "h-14 px-10 text-base rounded-xl",
        icon: "h-11 w-11 rounded-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };